import { Component } from '@theme/component';

/**
 * Reorders product cards and filler cards within a collection list row.
 * Filler cards (.fillerObject) define their position via data-order.
 *
 * Grid layout: assigns CSS order to reorder visually.
 * Carousel layout: physically moves DOM nodes so slide order is correct.
 */
export class NaCollectionListComponent extends Component {
    connectedCallback() {
        super.connectedCallback();
        this.#reorderCards();
    }

    updatedCallback() {
        this.#reorderCards();
    }

    #reorderCards() {
        const rows = this.querySelectorAll('.na-collection-list__row');
        rows.forEach((row) => {
            this.#reorderGrid(row);
            this.#reorderCarousel(row);
        });
    }

    /**
     * Grid layout — use CSS order property.
     * @param {Element} row
     */
    #reorderGrid(row) {
        const grid = row.querySelector('.resource-list--grid');
        if (!grid) return;

        const productCards = grid.querySelectorAll(':scope > .resource-list__item:not(.fillerObject)');
        const fillerCards = grid.querySelectorAll(':scope > .fillerObject');

        if (fillerCards.length === 0) return;

        const fillerPositions = Array.from(fillerCards).map((filler) => parseInt(filler.dataset.order, 10));

        let productOrder = 1;
        productCards.forEach((card) => {
            while (fillerPositions.includes(productOrder)) {
                productOrder++;
            }
            card.style.order = productOrder;
            productOrder++;
        });

        fillerCards.forEach((filler) => {
            const order = parseInt(filler.dataset.order, 10);
            if (!isNaN(order)) {
                filler.style.order = order;
            }
        });
    }

    /**
     * Carousel layout — physically reorder slide DOM nodes.
     * Finds slides containing .fillerObject elements and moves them
     * to the correct position based on data-order.
     * @param {Element} row
     */
    #reorderCarousel(row) {
        const slideshow = row.querySelector(
            '.resource-list__carousel slideshow-component, .resource-list__carousel .slideshow'
        );
        if (!slideshow) return;

        const slidesContainer = slideshow.querySelector('.slideshow__slides') || slideshow;
        const allSlides = Array.from(
            slidesContainer.querySelectorAll(':scope > .slideshow__slide, :scope > .resource-list__slide')
        );

        if (allSlides.length === 0) return;

        /** @type {Array<{slide: Element, targetIndex: number}>} */
        const fillerSlides = [];

        allSlides.forEach((slide) => {
            const filler = slide.querySelector('.fillerObject');
            if (filler) {
                const order = parseInt(filler.dataset.order, 10);
                if (!isNaN(order)) {
                    fillerSlides.push({ slide, targetIndex: order });
                }
            }
        });

        if (fillerSlides.length === 0) return;

        const productSlides = allSlides.filter((slide) => !slide.querySelector('.fillerObject'));

        const fillerPositions = fillerSlides.map((f) => f.targetIndex);

        /** @type {Array<Element>} */
        const ordered = [];
        let productIdx = 0;
        let position = 1;
        const totalSlides = productSlides.length + fillerSlides.length;

        while (ordered.length < totalSlides) {
            if (fillerPositions.includes(position)) {
                const filler = fillerSlides.find((f) => f.targetIndex === position);
                if (filler) {
                    ordered.push(filler.slide);
                }
            } else if (productIdx < productSlides.length) {
                ordered.push(productSlides[productIdx]);
                productIdx++;
            }
            position++;
        }

        while (productIdx < productSlides.length) {
            ordered.push(productSlides[productIdx]);
            productIdx++;
        }

        ordered.forEach((slide) => slidesContainer.appendChild(slide));
    }
}

if (!customElements.get('na-collection-list-component')) {
    customElements.define('na-collection-list-component', NaCollectionListComponent);
}
